import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "../lib/errors";

export default function MediaPicker({ open, onClose, onPick, allowDelete = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/media");
      setItems(res.data);
    } catch { toast.error("Could not load media"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size is 5MB"); return; }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setItems((prev) => [res.data, ...prev]);
      e.target.value = "";
    } catch (err) {
      toast.error(extractErrorMessage(err, "Upload failed"));
    } finally { setUploading(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this image? It cannot be undone.")) return;
    try {
      await api.delete(`/admin/media/${id}`);
      setItems(prev => prev.filter(x => x.media_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.media_id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* noop */ }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="media-picker">
        <DialogHeader>
          <DialogTitle>Media library</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b border-black/5 pb-3">
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="media-upload-btn">
            <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload image"}
          </Button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onUpload} data-testid="media-upload-input" />
          <span className="text-xs text-[#9CA3AF]">JPEG · PNG · WebP · GIF · 5MB max</span>
        </div>
        <div className="flex-1 overflow-y-auto py-3">
          {loading && <div className="text-sm text-[#9CA3AF] py-8 text-center">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-[#9CA3AF] py-12 text-center">No images yet — upload your first one above.</div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {items.map(item => (
              <div key={item.media_id} className="group relative aspect-square bg-[#F3F4F6] rounded-xl overflow-hidden border border-black/5 cursor-pointer" data-testid={`media-card-${item.media_id}`} onClick={() => onPick?.(item)}>
                <img src={item.url} alt={item.alt || item.original_filename} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                  <Button size="sm" className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid={`media-pick-${item.media_id}`} onClick={(e) => { e.stopPropagation(); onPick?.(item); }}>
                    Use this
                  </Button>
                  <div className="flex gap-1 mt-1">
                    <button type="button" onClick={(e) => { e.stopPropagation(); copyUrl(item); }} className="bg-white/90 hover:bg-white text-[#111827] text-xs px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`media-copy-${item.media_id}`}>
                      {copiedId === item.media_id ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> URL</>}
                    </button>
                    {allowDelete && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(item.media_id); }} className="bg-red-500/90 hover:bg-red-500 text-white text-xs px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`media-delete-${item.media_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                {item.original_filename && (
                  <div className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent truncate">
                    {item.original_filename}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-black/15">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
